import { AnimationEvent } from '../animation/AnimationState';
import { Gltf } from './GltfTypes';

const EXTENSION_KEY = '_ANIMATION_EVENTS';

/**
 * Skin-extras layout for per-clip animation events. Stored under
 * `skin.extras["_ANIMATION_EVENTS"]` as `{ events: Record<clipName, AnimationEvent[]> }`,
 * matching `AnimationStateExtras` siting (both attach to skin extras because
 * both share the authored-and-persisted-per-rig lifecycle). Read authored
 * per-clip animation events from a GLB's first skin extras. Returns `null`
 * when the model carries no events. Tolerates malformed payloads by
 * returning `null` rather than throwing — the caller falls back to the
 * engine's hardcoded per-state defaults.
 */
export function readAnimationEventsExtras(json: Gltf): Record<string, AnimationEvent[]> | null {
  const skin = json.skins?.[0];
  const raw = skin?.extras?.[EXTENSION_KEY] as { events?: unknown } | undefined;
  if (!raw || typeof raw !== 'object' || typeof raw.events !== 'object' || raw.events === null) {
    return null;
  }
  const out: Record<string, AnimationEvent[]> = {};
  for (const [clipName, list] of Object.entries(raw.events as Record<string, unknown>)) {
    if (!Array.isArray(list)) continue;
    const events: AnimationEvent[] = [];
    for (const item of list) {
      if (!item || typeof item !== 'object') continue;
      const rec = item as Record<string, unknown>;
      const at = rec['at'];
      const name = rec['name'];
      if (typeof at !== 'number' || typeof name !== 'string') continue;
      const event: AnimationEvent = { at, name };
      const clipId = rec['clipId'];
      if (typeof clipId === 'string' && clipId.length > 0) {
        event.clipId = clipId;
      }
      events.push(event);
    }
    if (events.length > 0) out[clipName] = events;
  }
  return Object.keys(out).length > 0 ? out : null;
}

// Note: there is no `writeAnimationEventsExtras` counterpart. GLB serialization
// of the `_ANIMATION_EVENTS` skin extras is owned by the backend
// `ConfigEmbedder` at publish time — the asset-manager never writes GLBs
// client-side. If a future flow needs to repair extras in-browser, mirror
// `writeAnimationStateExtras` (deep-clone + skin.extras swap).
